import type { ColumnOption } from "@pentacode/core";
import { columnConfigs, sortBy } from "@pentacode/core";
import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { shared } from "../styles";
import { Checkbox } from "./checkbox";
import "./popover";
import "./scroller";
import "./sortable-list";

export type ColumnSelectorChangedEvent = CustomEvent<{ columns: ColumnOption[] }>;

@customElement("ptc-column-selector")
export class ColumnSelector extends LitElement {
    @property({ type: Array })
    columns: ColumnOption[] = [];

    @query("#columnsForm")
    private _columnsForm: HTMLFormElement;

    public get displayColumns(): ColumnOption[] {
        const enabledTypes = new FormData(this._columnsForm).getAll("columns");
        return this.columns.map((column) => ({ ...column, disabled: !enabledTypes.includes(column.type) }));
    }

    private _handleChange() {
        const updatedColumns = this.displayColumns;
        // Preserve order from the current columns array
        const orderedColumns = sortBy(updatedColumns, (column) => column.order ?? Number.MAX_SAFE_INTEGER, "ascending");
        this.dispatchEvent(
            new CustomEvent<{ columns: ColumnOption[] }>("changed", {
                detail: { columns: orderedColumns },
            })
        );
    }

    private _handleColumnReorder(ev: CustomEvent) {
        const { fromIndex, toIndex } = ev.detail;

        // Get visible columns sorted by order
        const visibleColumns = this.columns
            .filter((column) => !columnConfigs[column.type].disabled)
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

        // Reorder the columns
        const [movedColumn] = visibleColumns.splice(fromIndex, 1);
        visibleColumns.splice(toIndex, 0, movedColumn);

        // Update order values
        const updatedColumns = this.columns.map((column) => {
            const newIndex = visibleColumns.findIndex((c) => c.type === column.type);
            if (newIndex !== -1) {
                return { ...column, order: newIndex };
            }
            return column;
        });

        this.dispatchEvent(
            new CustomEvent<{ columns: ColumnOption[] }>("changed", {
                detail: { columns: updatedColumns },
            })
        );
    }

    private _selectAll() {
        this._columnsForm.querySelectorAll("input").forEach((input) => {
            (input as HTMLInputElement).checked = true;
        });
        this._handleChange();
    }

    private _selectNone() {
        this._columnsForm.querySelectorAll("input").forEach((input) => {
            (input as HTMLInputElement).checked = false;
        });
        this._handleChange();
    }

    static styles = [
        shared,
        Checkbox.styles,
        css`
            .column-item {
                display: grid;
                grid-template-columns: min-content auto;
                gap: 0;
            }

            .column-item:hover .grab-handle {
                opacity: 1;
            }

            .grab-handle {
                cursor: grab;
                opacity: 0.5;
                place-self: center;
                transition: opacity 0.2s ease-in-out;
            }
        `,
    ];

    render() {
        return html`
            <button class="slim transparent" title="Spalten wählen">
                <ptc-ui-icon name="icon.toggle.select-all" class="large"></ptc-ui-icon>
            </button>

            <ptc-popover style="min-width: 10em">
                <form class="scroller" id="columnsForm" @change=${this._handleChange}>
                    <div style="display: flex; justify-content: space-evenly;">
                        <button type="button" @click=${this._selectNone} class="transparent slim">
                            <ptc-ui-icon name="icon.toggle.select-none"></ptc-ui-icon> Keine
                        </button>
                        <button type="button" @click=${this._selectAll} class="transparent slim">
                            <ptc-ui-icon name="icon.toggle.select-all"></ptc-ui-icon> Alle
                        </button>
                    </div>

                    <ptc-scroller style="max-height: 40em">
                        <ptc-sortable-list
                            .items=${this.columns
                                .filter((column) => !columnConfigs[column.type].disabled)
                                .sort(
                                    (a, b) =>
                                        (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
                                )}
                            .renderItem=${(column: ColumnOption) => {
                                const { type, disabled } = column;
                                const checked = !disabled;
                                return html`
                                    <div class="column-item">
                                        <ptc-ui-icon name="icon.action.reorder" class="grab-handle"></ptc-ui-icon>
                                        <ptc-checkbox-button
                                            .label=${columnConfigs[type].getHeader()}
                                            name="columns"
                                            .value=${type}
                                            ?checked=${checked}
                                            buttonClass="transparent slim"
                                        ></ptc-checkbox-button>
                                    </div>
                                `;
                            }}
                            @item-moved=${this._handleColumnReorder}
                        ></ptc-sortable-list>
                    </ptc-scroller>
                </form>
            </ptc-popover>
        `;
    }
}
